import { getMomentTz } from "@dexteel/mesf-core";
import { Chip } from "@mui/material";
import { ICellRendererParams } from "ag-grid-enterprise";
import type { Duration } from "moment";
import "moment-duration-format";

import { useQuery } from "@tanstack/react-query";
import { HeatAgeThreshold } from "../models/HeatAgeThreshold";
import { getHeatAgeThresholds } from "../repositories/HeatAgeThresholdsRepository";
import { HEAT_AGE_COLORS } from "../utils/heatAgeLevels";

const moment = getMomentTz();

export const getDaysSinceLastBillet = (campaign?: { End: Date | null }) => {
	if (!campaign?.End) return null;
	return moment().diff(moment(campaign.End), "days", true);
};

export const formatDaysSinceLastBillet = (days: number | null | undefined) => {
	if (days == null) return "-";
	const duration = moment.duration(days, "days") as Duration;
	return duration.format("d[d] h[h]", { trim: "both" });
};

const findAlert = (days: number, thresholds: HeatAgeThreshold[]) =>
	thresholds.find((threshold) => days > threshold.Days);

export const useHeatAgeThresholds = () =>
	useQuery<HeatAgeThreshold[], Error>({
		queryKey: ["heatAgeThresholds"],
		queryFn: ({ signal }) => getHeatAgeThresholds(signal),
		staleTime: 30 * 60 * 1000,
	});

export const TimeSinceLastBilletCell = ({
	value,
	data,
}: ICellRendererParams<{ SampleStatusCode?: "P" | "R" | "U" | null }>) => {
	const { data: thresholds } = useHeatAgeThresholds();

	if (value == null) return <>-</>;

	const alert =
		data?.SampleStatusCode === "P"
			? undefined
			: findAlert(value, thresholds ?? []);

	if (!alert) return <>{formatDaysSinceLastBillet(value)}</>;

	return (
		<Chip
			label={formatDaysSinceLastBillet(value)}
			size="small"
			sx={{
				height: 18,
				fontSize: "0.65rem",
				fontWeight: 600,
				...HEAT_AGE_COLORS[alert.Level],
				"& .MuiChip-label": { px: 0.75 },
			}}
		/>
	);
};
